// WorkflowBuilder type definitions

import { Node } from '@xyflow/react';
import { MouseEvent as ReactMouseEvent } from 'react';
import { PartyStatusDefinition, StatusTransition, TransferExperience } from 'services/api/WorkflowServiceAPI';
import { QualificationsResponse } from 'services/api/AgentCenterAPI';

// ============================================================================
// Operating Hours Types
// ============================================================================

export interface OperatingHoursSettings {
    startTime: string;
    endTime: string;
    timezone: string;
}

// ============================================================================
// Queue & Cadence Types
// ============================================================================

export type PrioritizationMode = 'cadence_completeness' | 'even_distribution';

/**
 * Shape stored under `workflow.config.queueCadence`. Every field is optional:
 * the backend treats an absent field as "feature off" (legacy behaviour), so
 * only the fields we send are written.
 */
export interface QueueCadenceSettings {
    prioritizationMode?: PrioritizationMode;
    maxCallsPerDay?: number;
    sameDayEnforcement?: boolean;
    /** "HH:MM", 24-hour. */
    businessDayEnd?: string;
    /** Percentage, 0-100. */
    queueDelayAlertThreshold?: number;
    retryTimeShiftEnabled?: boolean;
}

/**
 * The same six settings as flat form fields. Every field carries an "unset"
 * value ('' for the four text-backed fields, null for the two toggles),
 * because a workflow that stores no queueCadence has to render blank and
 * unchecked. Borrowing the create-time defaults instead would write settings
 * nobody chose the next time anyone saved the workflow.
 *
 * The two numeric settings are held as strings, the way the inputs backing
 * them actually hold a value, so '' is the single unset marker across all four
 * and there is no NaN state to reason about. buildQueueCadencePayload parses
 * them back on the way out.
 */
export interface QueueCadenceFormValues {
    prioritizationMode: PrioritizationMode | '';
    maxCallsPerDay: string;
    sameDayEnforcement: boolean | null;
    businessDayEnd: string;
    queueDelayAlertThreshold: string;
    retryTimeShiftEnabled: boolean | null;
}

// ============================================================================
// SMS Pacing Types
// ============================================================================

/**
 * Shape stored under `workflow.config.smsPacing`. Absent means the workflow
 * falls back to the backend's default (6 texts per minute).
 */
export interface SmsPacingSettings {
    maxPerMinute?: number;
}

/** Shape stored under `workflow.config.numberLimits`. Absent = skipWarmup false, dailyCallCap 100. */
export interface NumberLimitsSettings {
    skipWarmup?: boolean;
    /** Daily calls per fully active number, 1-1000. */
    dailyCallCap?: number;
}

export interface WorkflowConfig {
    operatingHours?: OperatingHoursSettings;
    crmCreateContactOnInbound?: boolean;
    queueCadence?: QueueCadenceSettings;
    smsPacing?: SmsPacingSettings;
    numberLimits?: NumberLimitsSettings;
    // Set at workflow creation and fixed after; the update endpoint rejects
    // both. Absent for tenants whose statuses come from dedicated code.
    partyStatuses?: PartyStatusDefinition[];
    statusTransitions?: StatusTransition[];
    dispositionLabels?: Record<string, string>;
    transferExperience?: TransferExperience;
    /** Prompt for inbound calls. Absent means Auto (first call node's prompt). */
    inboundPromptId?: string;
}

// ============================================================================
// Node Data Types
// ============================================================================

export interface BaseNodeData extends Record<string, unknown> {
    label: string;
    subLabel?: string;
    icon?: string;
    onDelete?: (nodeId: string, event: ReactMouseEvent) => void | boolean;
}

export enum WorkflowTagsType {
    PREDEFINED = 'PREDEFINED',
    GENERIC = 'GENERIC',
}

export interface CallNodeData extends BaseNodeData {
    promptId: string;
    promptName: string;
    aiPrompt?: string; // For backward compatibility
    callText: string;
    tagsType: WorkflowTagsType;
    leaveVoicemail?: boolean;
}

export interface DelayNodeData extends BaseNodeData {
    delayType: 'TIMESTAMP' | 'DURATION';
    specificDateTime: string; // ISO string
    timeValue: string;
    timeUnit: 'minutes' | 'hours' | 'days';
}

export type SMSMode = 'static' | 'agentic';

export type TimeoutUnit = 'hours' | 'minutes';

export interface SMSNodeData extends BaseNodeData {
    smsMode: SMSMode;
    messageContent: string;
    langfusePromptId: string;
    userAbandonedTimeoutHours?: number;
    agentAbandonedTimeoutHours?: number;
    userAbandonedTimeoutMinutes?: number;
    agentAbandonedTimeoutMinutes?: number;
    userAbandonedTimeoutUnit?: TimeoutUnit;
    agentAbandonedTimeoutUnit?: TimeoutUnit;
    expectResponse: boolean;
}

export interface EmailNodeData extends BaseNodeData {
    messageContent: string;
}

export interface LeadNodeData extends BaseNodeData {
    crmStatus: string;
}

export type DecisionFieldType = 'call_disposition' | 'call_status' | 'party_stage' | 'sms_status';

/** One row in the DECISION node API `config.conditions` array. */
export interface DecisionCondition {
    decisionField: DecisionFieldType;
    values: string[];
    nextNode: string;
}

export interface DecisionNodeData extends BaseNodeData {
    conditions: DecisionCondition[];
    defaultNextNode?: string | null;
}

/** Editable row in the decision config modal (before nextNode ids exist). */
export type DecisionConditionFormRow = {
    decisionField: DecisionFieldType;
    values: string[];
    nextNodeType: 'CALL' | 'SMS' | 'DELAY' | 'END';
};

/** Terminal node – no config, no modal. Used as mapping target (e.g. UNQUALIFIED → End). */
export type EndNodeData = BaseNodeData;

export type NodeData =
    | BaseNodeData
    | CallNodeData
    | DelayNodeData
    | SMSNodeData
    | EmailNodeData
    | LeadNodeData
    | DecisionNodeData
    | EndNodeData;

// ============================================================================
// Node Types
// ============================================================================

export type NodeType =
    | 'defaultNode'
    | 'callNode'
    | 'delayNode'
    | 'smsNode'
    | 'emailNode'
    | 'leadNode'
    | 'decisionNode'
    | 'endNode'
    /** Main-canvas-only synthetic anchor; not persisted */
    | 'terminalBridgeNode';

export type ServerNodeType = 'CALL' | 'DELAY' | 'SMS' | 'EMAIL' | 'LEAD' | 'DECISION' | 'END' | 'UNKNOWN';

// ============================================================================
// Server Workflow Types
// ============================================================================

export interface ServerNodeConfig {
    delay_type?: 'TIMESTAMP' | 'DURATION';
    delay?: {
        timestamp?: string;
        duration?: number;
        unit?: 'MINUTES' | 'HOURS' | 'DAYS';
    };
    promptId?: string;
    promptName?: string;
    aiPrompt?: string;
    callText?: string;
    leaveVoicemail?: boolean;
    smsText?: string;
    messageContent?: string;
    smsMode?: 'static' | 'agentic';
    langfusePromptId?: string;
    userAbandonedTimeoutHours?: number;
    agentAbandonedTimeoutHours?: number;
    userAbandonedTimeoutMinutes?: number;
    agentAbandonedTimeoutMinutes?: number;
    userAbandonedTimeoutUnit?: TimeoutUnit;
    agentAbandonedTimeoutUnit?: TimeoutUnit;
    expectResponse?: boolean;
    crmStatus?: string;
    nextNode?: string | null;
    /** @deprecated Legacy DECISION shape; prefer `conditions`. */
    decisionField?: DecisionFieldType;
    /** @deprecated Legacy DECISION shape; prefer `conditions`. */
    mappings?: Record<string, { nextNode: string }>;
    conditions?: DecisionCondition[];
    defaultNextNode?: string | null;
}

export interface ServerNode {
    id: string;
    type: ServerNodeType;
    config: ServerNodeConfig;
    uiConfig: {
        dragging?: boolean;
        height?: number;
        position: { x: number; y: number };
        positionAbsolute?: { x: number; y: number };
        selected?: boolean;
        width?: number;
        label?: string;
    };
}

export interface WorkflowDefinition {
    nodes: ServerNode[];
    generic_tags?: Record<string, { type: string; prompt: string; order?: number }>;
    generic_prompts?: Record<string, string>;
    [key: string]: unknown; // Allow additional properties
}

export interface WorkflowData {
    id?: string;
    title: string;
    description: string;
    start_date: string | null;
    end_date: string | null;
    country_code?: string;
    language?: string;
    workflow_definition?: WorkflowDefinition;
    head_node_id?: string;
    /** Terminal transition DECISION node id (canonical). */
    terminal_decision_node_id?: string | null;
    deleted_node_ids?: string[] | null;
    // Request: WorkflowDefinition (nodes). Response: WorkflowConfig (operatingHours).
    // The backend uses different field semantics for the same JSON key in each direction.
    config?: WorkflowDefinition | WorkflowConfig;
    tag_type: WorkflowTagsType;
    human_transfer: boolean;
    operatingHours?: OperatingHoursSettings;
    allow_calls_without_online_agents?: boolean;
    /**
     * NANP area code (100-999) used to purchase the workflow's initial phone
     * number on create. Required on create for tenants with telephony configured;
     * ignored on update (the backend uses AreaCode from WorkflowCreateRequest only).
     */
    area_code?: number;
    /** Create only: explicit inbound prompt, so provisioning builds the dispatch rule with it. */
    inbound_prompt_id?: string;
}

// ============================================================================
// Prompt Types
// ============================================================================

export interface Prompt {
    id: string;
    prompt_name: string;
}

export interface PromptsResponse {
    prompts: Prompt[];
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface NodeComponentProps {
    id: string;
    data: NodeData;
}

export interface NodeConfigModalProps {
    node: Node<NodeData>;
    nodes?: Node<NodeData>[];
    /** When set, identifies the terminal routing decision (for dual-canvas terminal flow). */
    terminalDecisionNodeId?: string | null;
    onClose: () => void;
    onSave: (id: string, updates: Partial<NodeData>) => void;
    onDecisionDone?: (
        nodeId: string,
        rows: DecisionConditionFormRow[],
        defaultNextNodeType: 'CALL' | 'SMS' | 'DELAY' | 'END' | null
    ) => void;
}

export interface ActionItem {
    label: string;
    type: NodeType;
}

// ============================================================================
// Connection Types
// ============================================================================

export interface ConnectionStartParams {
    nodeId: string | null;
    handleId: string | null;
    handleType: 'source' | 'target' | null;
}

// ============================================================================
// Notification Types
// ============================================================================

export interface NotificationState {
    isOpen: boolean;
    message: string;
    title: string;
    type: 'success' | 'error' | 'warning' | 'info';
    callback: (() => () => void) | null;
}

// ============================================================================
// Validation Types
// ============================================================================

export interface ValidationResult {
    valid: boolean;
    issues: string[];
}

export interface TestGenericQualificationConfig {
    generic_tags: Record<string, { type: string; prompt: string; order?: number }>;
    generic_prompts: Record<string, string>;
}

export interface TestGenericQualificationRequest {
    transcript: string;
    config: TestGenericQualificationConfig;
}

export interface TestGenericQualificationResponse {
    qualification: {
        qualified: boolean;
        undetermined: boolean;
        unqualified: boolean;
    };
    extraction: { tags: QualificationsResponse[] };
}
